import type {
    OnFilterActiveContext,
    OnCompileContext,
    ObjectPath,
    EvaluatedTemplateInputs,
    EvaluatedTemplateOutputs,
    BuilderOutlet,
    GraphBuilderScope,
    NodeEvaluator,
    NotReady,
    InterfaceDescriptor,
    TemplateNode,
    FundamentalLikeNode,
    TypeDescriptors,
    MaterialAssignment,
    MaterialInput,
    MeshGeometryAttributeSetLambda,
    MeshExport,
    GeometryGraphMesh,
    NumberLikeNode,
} from "@cm/template-nodes"
import type {MattressTexUVLayout} from "#mattress/geometry/mattress"
import type {MattressCurve, MattressCurveData} from "#mattress/nodes/curve/mattress-curve"
import type {MattressReferenceFrame, MattressReferenceFrameData} from "#mattress/nodes/reference-frame/mattress-reference-frame"
import type {MattressReferenceFrameInput} from "#mattress/nodes/reference-frame/mattress-reference-frame-input"
import type {MattressReferenceFrameOutput} from "#mattress/nodes/reference-frame/mattress-reference-frame-output"
import type {MattressComputedCurve} from "#mattress/nodes/curve/mattress-computed-curve"
import type {MattressCut} from "#mattress/nodes/mattress-cut"
import type {MattressComponentInstance} from "#mattress/nodes/component/mattress-component-instance"
import type {MattressComponentGraph} from "#mattress/nodes/component/mattress-component-graph"
import type {MattressComponentGraphGenerator} from "#mattress/nodes/component/mattress-component-graph-generator"
import type {MattressCurveLikeNode, MattressReferenceFrameLikeNode} from "#mattress/role-markers"
import type {MattressComponentInterfaceDescriptor} from "#mattress/nodes/component/mattress-component-interface-descriptors"
import type {MATTRESS_BASE_MESH} from "#mattress/validation"

import {isNodeClass} from "@cm/graph-v3"
// `import type`: a value import of MCI would form the cycle MCI → CompileRunMattressComponent →
// mattress-component-types → MCI. `instanceof` checks use string identity (below) instead.

const isMattressComponentInstance = isNodeClass<MattressComponentInstance>("MattressComponentInstance")

import type {MaterialGraph} from "@cm/material-nodes"

export type MattressMeshGeometryParameterKey =
    | "width"
    | "length"
    | "height"
    | "cornerRadius"
    | "topRadius"
    | "bottomRadius"
    | "cornerRadiusSegments"
    | "topRadiusSegments"
    | "bottomRadiusSegments"
    | "widthSegments"
    | "heightSegments"
    | "lengthSegments"
    | "resolutionMultiplier"

export type MattressMeshGeometryParametersData = {[K in MattressMeshGeometryParameterKey]: number}

export type MattressMeshGeometryParametersLocalNodes = {[K in MattressMeshGeometryParameterKey]: NumberLikeNode}

export type MattressMeshBaseMeshLocalNodes = {
    attributes: MeshGeometryAttributeSetLambda
    export: MeshExport
    mesh: GeometryGraphMesh
}

export type MattressMeshSegmentedMeshLocalNodes = MattressMeshBaseMeshLocalNodes

export type MattressMaterialAssignmentData = {
    localNode: MaterialAssignment
    localMaterialInput: MaterialInput
    sourceNode: TemplateNode | null
    materialData: MaterialGraph | null
}

export type MattressCutData = {
    texUVLayout: MattressTexUVLayout
    conditions: {side: "left" | "right"; curveData: MattressCurveData}[]
    materialAssignmentData: MattressMaterialAssignmentData | null
    path: MattressNodePath
}

export type MattressMeshData = {
    geometryParameters: MattressMeshGeometryParametersData | null
    geometryParametersLocalNodes: MattressMeshGeometryParametersLocalNodes | null
    geometryParameterSourceNodes: {[localId: string]: TemplateNode} | null
    baseMeshLocalNodes: MattressMeshBaseMeshLocalNodes | null
    segmentedMeshLocalNodes: MattressMeshSegmentedMeshLocalNodes | null
    segmentedMeshMaterialSlotsOwners: {[materialID: number]: MattressNodePath | null} | null
    segmentedMeshMaterialAssignments: {[materialID: number]: MattressMaterialAssignmentData | null} | null
}

//////////////////////////////////////////////////////////////////////////////////////////////

export type MattressOnFilterActiveContext = OnFilterActiveContext & {
    mattressContext?: {
        root: MattressComponentGraph
        isComponentCompilationVisit: true
    }
}

export type MattressNodePath = (
    | MattressComponentInstance
    | MattressCurve
    | MattressReferenceFrame
    | MattressReferenceFrameInput
    | MattressComputedCurve
    | MattressCut
    | MattressComponentGraphGenerator
    | MattressReferenceFrameOutput
)[]

export type MattressFocusedComponentInstancePath = MattressComponentInstance[] | "root"

export function isMattressFocusedComponentInstancePath(value: unknown): value is MattressFocusedComponentInstancePath {
    if (value === "root") return true
    if (!Array.isArray(value)) return false
    if (value.length === 0) return false
    return value.every(isMattressComponentInstance)
}

export type MattressLocalNodesDataBase = {
    nodes: TemplateNode[]
    path: MattressNodePath
    inputsData?: EvaluatedTemplateInputs
}

export type MattressCurveLocalNodesData = MattressLocalNodesDataBase & {
    readonly type: "mattressCurve"
}
export type MattressReferenceFrameLocalNodesData = MattressLocalNodesDataBase & {
    readonly type: "mattressReferenceFrame"
}
export type MattressComponentInstanceLocalNodesData = MattressLocalNodesDataBase & {
    readonly type: "mattressComponentInstance"
    localSubInstance?: TemplateNode
}
export type MattressLocalNodesData = MattressCurveLocalNodesData | MattressReferenceFrameLocalNodesData | MattressComponentInstanceLocalNodesData

export function isMattressComponentInstanceLocalNodesData(data: MattressLocalNodesData): data is MattressComponentInstanceLocalNodesData {
    return data.type === "mattressComponentInstance"
}

export function isMattressCurveLocalNodesData(data: MattressLocalNodesData): data is MattressCurveLocalNodesData {
    return data.type === "mattressCurve"
}

export type MattressInfoDataBase = {
    path: MattressNodePath
}

export type MattressComponentGraphGeneratorInfoData = MattressInfoDataBase & {
    readonly type: "mattressComponentGraphGenerator"
    componentDescriptorList: MattressComponentInterfaceDescriptor<unknown>[]
    templateDescriptorList: InterfaceDescriptor<unknown>[]
}

export type MattressInfoData = MattressComponentGraphGeneratorInfoData

export const isMattressComponentGraphGeneratorInfoData = (data: MattressInfoData): data is MattressComponentGraphGeneratorInfoData =>
    data.type === "mattressComponentGraphGenerator"

export type MattressOnCompileContext = OnCompileContext & {
    mattressContext: {
        nodePath: MattressNodePath
        objectPath: ObjectPath
        componentDepth: number
        componentInputs: EvaluatedMattressComponentInputs
        exposeClaimedSubComponentInputs: boolean
        evaluatePartialMattressMeshData: (scope: GraphBuilderScope, evaluator: NodeEvaluator) => BuilderOutlet<MattressMeshData | null>
        forwardedLocalInstanceTemplateOutputs: EvaluatedTemplateOutputs
        forwardedLocalInstanceDescriptorList: InterfaceDescriptor<unknown>[]
        evaluatedData: {
            localNodesDataLists: BuilderOutlet<MattressLocalNodesData[]>[]
            curvesDataLists: BuilderOutlet<MattressCurveData[]>[]
            referenceFramesDataLists: BuilderOutlet<MattressReferenceFrameData[]>[]
            cutsDataLists: BuilderOutlet<MattressCutData[]>[]
            componentsDescriptorLists: BuilderOutlet<MattressComponentInterfaceDescriptor<unknown>[]>[]
            templatesDescriptorLists: BuilderOutlet<InterfaceDescriptor<unknown>[]>[]
            inputSourceMappings: {mapping: Map<string, TemplateNode[]>; rawDescriptorList: BuilderOutlet<InterfaceDescriptor<unknown>[]>; nodeId: string}[]
            componentOutputs: EvaluatedMattressComponentOutputs
            subComponentsOutputs: BuilderOutlet<EvaluatedMattressComponentOutputs>[]
            subTemplatesOutputs: BuilderOutlet<EvaluatedTemplateOutputs>[]
            infoDataLists: BuilderOutlet<MattressInfoData[]>[]
        }
    }
}

//////////////////////////////////////////////////////////////////////////////////////////////

type MattressBaseMesh = typeof MATTRESS_BASE_MESH

export type MattressBaseMeshData = {
    localNode: FundamentalLikeNode<unknown>
}

//////////////////////////////////////////////////////////////////////////////////////////////

export type EvaluatedMattressComponentValueOrigin = MattressCurveLikeNode | MattressReferenceFrameLikeNode | MattressBaseMesh | TemplateNode
export type EvaluatedMattressComponentValueType = "mattressCurve" | "mattressReferenceFrame" | "mattressBaseMesh" | "unknown"
export type EvaluatedMattressComponentValue = MattressCurveData | MattressReferenceFrameData | MattressBaseMeshData

export type EvaluatedMattressComponentInput<
    T extends EvaluatedMattressComponentValueType = EvaluatedMattressComponentValueType,
    O extends EvaluatedMattressComponentValueOrigin = EvaluatedMattressComponentValueOrigin,
    V extends EvaluatedMattressComponentValue | null = EvaluatedMattressComponentValue | null,
> = {
    type: T
    origin?: O | undefined
    value: V
}
export type EvaluatedMattressComponentInputs = {[inputId: string]: EvaluatedMattressComponentInput | undefined | typeof NotReady}
export type EvaluatedMattressComponentOutputs = {[outputId: string]: any}

export type MattressComponentGraphData = {
    graph: MattressComponentGraph
    contentHash: string
}

export const EvaluatedMattressComponentInputsTypeDescriptor: TypeDescriptors.Desc<EvaluatedMattressComponentInputs> = {
    deepCompare: 2,
}

export const EvaluatedTemplateInputsTypeDescriptor: TypeDescriptors.Desc<EvaluatedTemplateInputs> = {
    deepCompare: 2,
}
